import { ItemDaTabelaDePreco, ItemDaVenda } from '@/models'
import FindRegistroDeCustosUseCase from '../custos/FindRegistroDeCustosUseCase'
import { FindItemDaTabelaDePrecoUseCase } from './FindItemDaTabelaDePrecoUseCase'

const TAMANHO_DO_BLOCO_DE_PRODUTOS = 200
const RESPOSTA_DO_BACKEND_SEM_CUSTO_REAL = '' as unknown as number

type CarregarItensDaTabela = (idTabela: string, produtosIds: string[]) => Promise<ItemDaTabelaDePreco[]>
type CarregarPrecosPorProdutos = (lojaId: string, produtosIds: string[]) => Promise<Record<string, number>>
type CarregarPrecosPorSkus = (lojaId: string, skus: string[]) => Promise<Record<string, number>>

function dividirEmBlocos<T>(itens: T[], tamanhoDoBloco: number): T[][] {
	const blocos: T[][] = []
	for (let inicio = 0; inicio < itens.length; inicio += tamanhoDoBloco) {
		blocos.push(itens.slice(inicio, inicio + tamanhoDoBloco))
	}
	return blocos
}

export class FindItemDaTabelaDePrecoEmLoteUseCase extends FindItemDaTabelaDePrecoUseCase {
	private produtosConsultados: Set<string>
	private carregarItensDaTabela: CarregarItensDaTabela
	private itensPorTabela = new Map<string, Promise<Map<string, ItemDaTabelaDePreco> | null>>()

	constructor(produtosIds: string[], carregarItensDaTabela?: CarregarItensDaTabela) {
		super()
		this.produtosConsultados = new Set(produtosIds)
		this.carregarItensDaTabela = carregarItensDaTabela
			?? ((idTabela, ids) => this.buscaItensPorProdutosDaTabelaDePreco(idTabela, ids))
	}

	async getByIdDoProduto(idTabela: string, id: string): Promise<ItemDaTabelaDePreco> {
		if (!this.produtosConsultados.has(id)) return super.getByIdDoProduto(idTabela, id)

		const itensDaTabela = await this.itensDaTabela(idTabela)
		if (!itensDaTabela) return super.getByIdDoProduto(idTabela, id)

		const item = itensDaTabela.get(id)
		if (!item) throw new Error('Produto não encontrado na tabela de preço')
		return item
	}

	private itensDaTabela(idTabela: string): Promise<Map<string, ItemDaTabelaDePreco> | null> {
		const jaCarregando = this.itensPorTabela.get(idTabela)
		if (jaCarregando) return jaCarregando

		const carregamento = this.carregarTodosOsItensDaTabela(idTabela)
		this.itensPorTabela.set(idTabela, carregamento)
		return carregamento
	}

	private async carregarTodosOsItensDaTabela(idTabela: string): Promise<Map<string, ItemDaTabelaDePreco> | null> {
		try {
			const blocos = dividirEmBlocos([...this.produtosConsultados], TAMANHO_DO_BLOCO_DE_PRODUTOS)
			const respostas = await Promise.all(blocos.map(bloco => this.carregarItensDaTabela(idTabela, bloco)))

			const itensPorProduto = new Map<string, ItemDaTabelaDePreco>()
			respostas.forEach(itens => itens.forEach(item => itensPorProduto.set(item.produto.id as string, item)))
			return itensPorProduto
		} catch {
			return null
		}
	}
}

export class FindRegistroDeCustosEmLoteUseCase extends FindRegistroDeCustosUseCase {
	private produtosConsultados: Set<string>
	private skusConsultados: Set<string>
	private carregarPrecosPorProdutos: CarregarPrecosPorProdutos
	private carregarPrecosPorSkus: CarregarPrecosPorSkus
	private precosDeCustoPorLoja = new Map<string, Promise<Record<string, number> | null>>()
	private precosReaisPorLoja = new Map<string, Promise<Record<string, number> | null>>()

	constructor(
		produtosIds: string[],
		skus: string[],
		carregarPrecosPorProdutos?: CarregarPrecosPorProdutos,
		carregarPrecosPorSkus?: CarregarPrecosPorSkus,
	) {
		super()
		this.produtosConsultados = new Set(produtosIds)
		this.skusConsultados = new Set(skus)
		this.carregarPrecosPorProdutos = carregarPrecosPorProdutos
			?? ((lojaId, ids) => this.findPrecosDeCustoPorProdutos(lojaId, ids))
		this.carregarPrecosPorSkus = carregarPrecosPorSkus
			?? ((lojaId, listaDeSkus) => this.findPrecosDeCustoRealPorSkus(lojaId, listaDeSkus))

		const consultarPrecoDeCustoIndividual = this.findPrecoDeCusto
		const consultarCustoRealIndividual = this.findPrecoDeCustoRealByLojaId

		this.findPrecoDeCusto = async (params?: any) => {
			const produtoId = params?.produtoId
			if (!this.produtosConsultados.has(produtoId)) return consultarPrecoDeCustoIndividual(params)

			const precos = await this.precosDeCusto(params.lojaId)
			const preco = precos ? precos[produtoId] : undefined
			return preco === undefined ? consultarPrecoDeCustoIndividual(params) : preco
		}

		this.findPrecoDeCustoRealByLojaId = async (lojaId: string, sku: string) => {
			if (!this.skusConsultados.has(sku)) return consultarCustoRealIndividual(lojaId, sku)

			const precos = await this.precosReais(lojaId)
			if (!precos) return consultarCustoRealIndividual(lojaId, sku)

			const preco = precos[sku]
			return preco === undefined || preco === null ? RESPOSTA_DO_BACKEND_SEM_CUSTO_REAL : preco
		}
	}

	private precosDeCusto(lojaId: string): Promise<Record<string, number> | null> {
		const jaCarregando = this.precosDeCustoPorLoja.get(lojaId)
		if (jaCarregando) return jaCarregando

		const carregamento = this.carregarEmBlocos([...this.produtosConsultados], bloco => this.carregarPrecosPorProdutos(lojaId, bloco))
		this.precosDeCustoPorLoja.set(lojaId, carregamento)
		return carregamento
	}

	private precosReais(lojaId: string): Promise<Record<string, number> | null> {
		const jaCarregando = this.precosReaisPorLoja.get(lojaId)
		if (jaCarregando) return jaCarregando

		const carregamento = this.carregarEmBlocos([...this.skusConsultados], bloco => this.carregarPrecosPorSkus(lojaId, bloco))
		this.precosReaisPorLoja.set(lojaId, carregamento)
		return carregamento
	}

	private async carregarEmBlocos(
		chaves: string[],
		carregarBloco: (bloco: string[]) => Promise<Record<string, number>>,
	): Promise<Record<string, number> | null> {
		try {
			const blocos = dividirEmBlocos(chaves, TAMANHO_DO_BLOCO_DE_PRODUTOS)
			const respostas = await Promise.all(blocos.map(bloco => carregarBloco(bloco)))
			return respostas.reduce((todos, resposta) => ({ ...todos, ...resposta }), {} as Record<string, number>)
		} catch {
			return null
		}
	}
}

export function criarConsultasDePrecoEmLote(itens: Array<Pick<ItemDaVenda, 'produto'>>) {
	const produtosIds = new Set<string>()
	const skus = new Set<string>()

	itens.forEach(({ produto }) => {
		if (produto.id) produtosIds.add(produto.id)
		if (produto.sku) skus.add(produto.sku)
	})

	return {
		findItemTabelaDePrecoUseCase: new FindItemDaTabelaDePrecoEmLoteUseCase([...produtosIds]),
		findRegistroDeCustosUseCase: new FindRegistroDeCustosEmLoteUseCase([...produtosIds], [...skus]),
	}
}
